import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  CreditCard, Shield, Users, DollarSign, Save,
  AlertTriangle, CheckCircle, XCircle, Clock, Building2, RefreshCw, Lock, Unlock
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { usePricing } from '../contexts/PricingContext';

const AdminMembershipSettings = () => {
  const [settings, setSettings] = useState({
    require_valid_cc: false,
    grace_period_days: 14,
    overage_price_per_customer: 5,
    overage_price_per_user: 10,
  });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enforcement, setEnforcement] = useState(null);
  const [togglingEnforcement, setTogglingEnforcement] = useState(false);
  const { refresh: refreshPricing } = usePricing();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, summaryRes, enforcementRes] = await Promise.all([
        api.get('/admin/membership/settings'),
        api.get('/admin/membership/summary'),
        api.get('/admin/pricing-enforcement'),
      ]);
      setSettings(settingsRes.data);
      setSummary(summaryRes.data);
      setEnforcement(enforcementRes.data);
    } catch (err) {
      toast.error('Failed to load membership settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/membership/settings', settings);
      toast.success('Membership settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnforcement = async (enabled) => {
    if (enabled === enforcement?.enabled) return;
    setTogglingEnforcement(true);
    try {
      const res = await api.put('/admin/pricing-enforcement', { enabled });
      setEnforcement(res.data);
      await refreshPricing();
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing enforcement');
    } finally {
      setTogglingEnforcement(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color = 'blue', sub }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <span className="text-sm font-medium text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Membership Settings</h1>
            <p className="text-gray-500 mt-1">Configure billing requirements, grace periods, and overage pricing</p>
          </div>

          {/* Pricing Enforcement — master switch */}
          {enforcement && (
            <div
              className={`rounded-xl border-2 overflow-hidden ${
                enforcement.enabled ? 'border-green-300 bg-green-50/40' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {enforcement.enabled
                        ? <Lock className="w-5 h-5 text-green-700" />
                        : <Unlock className="w-5 h-5 text-gray-500" />}
                      <h2 className="text-lg font-semibold text-gray-900">Enforce Pricing</h2>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          enforcement.enabled
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                        data-testid="enforcement-state"
                      >
                        {enforcement.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 ml-7 leading-relaxed">
                      {enforcement.enabled ? (
                        <>
                          Subscriptions are required. Companies without an active plan
                          (and past their grace period) are locked out of the platform,
                          seat limits apply, and LOI intel is restricted to paying
                          customers. Pricing is visible across the site.
                        </>
                      ) : (
                        <>
                          The platform is running free. Nothing is blocked, seat limits
                          and the LOI paywall are off, and all pricing is hidden from the
                          site. <strong>Companies that already have an active
                          subscription keep it and continue to be billed.</strong>
                        </>
                      )}
                    </p>
                    <div className="ml-7 mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
                      <span><strong className="text-gray-700">{enforcement.entitled_companies}</strong> companies subscribed</span>
                      <span><strong className="text-gray-700">{enforcement.unentitled_companies}</strong> without a subscription</span>
                      <span>Grace period: <strong className="text-gray-700">{enforcement.grace_period_days} days</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      disabled={togglingEnforcement}
                      onClick={() => handleToggleEnforcement(true)}
                      data-testid="enforcement-enable"
                      className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-50 ${
                        enforcement.enabled
                          ? 'bg-green-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Enable
                    </button>
                    <button
                      type="button"
                      disabled={togglingEnforcement}
                      onClick={() => handleToggleEnforcement(false)}
                      data-testid="enforcement-disable"
                      className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-50 ${
                        !enforcement.enabled
                          ? 'bg-gray-800 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Disable
                    </button>
                  </div>
                </div>

                {!enforcement.enabled && enforcement.entitled_companies > 0 && (
                  <div className="mt-4 ml-7 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    {enforcement.entitled_companies} existing subscription(s) are still
                    billing. Cancel them individually in Stripe if you want billing to
                    stop as well — turning enforcement off never cancels a subscription.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary Stats */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <StatCard icon={Building2} label="Total Companies" value={summary.total_companies} color="blue" />
              <StatCard icon={CheckCircle} label="Active Subscriptions" value={summary.active_subscriptions} color="green" />
              <StatCard icon={Clock} label="In Trial" value={summary.trial_subscriptions} color="amber" />
              <StatCard icon={AlertTriangle} label="Past Due" value={summary.past_due} color="red" />
              <StatCard
                icon={DollarSign}
                label="MRR"
                value={`$${summary.mrr?.toLocaleString() || 0}`}
                color="green"
                sub={`ARR: $${summary.arr?.toLocaleString() || 0}`}
              />
            </div>
          )}

          {/* Additional Stats Row */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={CreditCard} label="Valid CC" value={summary.cc_valid_companies} color="green" />
              <StatCard icon={Shield} label="Paid Companies" value={summary.paid_companies} color="blue" />
              <StatCard icon={Clock} label="In Grace Period" value={summary.grace_period_companies} color="amber" />
              <StatCard icon={XCircle} label="Cancelled" value={summary.cancelled} color="gray" />
            </div>
          )}

          {/* Settings Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Billing Requirements</h2>
              <p className="text-sm text-gray-500 mt-1">Control how payment validation works for your platform</p>
            </div>

            <div className="p-6 space-y-8">
              {/* Require Valid CC */}
              <div className="flex items-start justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gray-700" />
                    <h3 className="font-semibold text-gray-900">Require Valid Credit Card</h3>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 ml-7">
                    When enabled, company admins must have a valid credit card on file to use the platform.
                    Free plans are exempt from this requirement.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.require_valid_cc}
                    onChange={(e) => setSettings({ ...settings, require_valid_cc: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Grace Period */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-gray-700" />
                  <h3 className="font-semibold text-gray-900">Grace Period</h3>
                </div>
                <p className="text-sm text-gray-500 mb-3 ml-7">
                  Number of days a company can use the system after their credit card fails or expires.
                  A warning banner is shown during this period.
                </p>
                <div className="ml-7 flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="90"
                    value={settings.grace_period_days}
                    onChange={(e) => setSettings({ ...settings, grace_period_days: parseInt(e.target.value) || 0 })}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              </div>

              {/* Divider */}
              <hr className="border-gray-100" />

              {/* Overage Pricing */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-gray-700" />
                  <h3 className="font-semibold text-gray-900">Overage Pricing</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4 ml-7">
                  Additional charges for customers and admin users that exceed the plan's allocated limits.
                  Charged monthly alongside the membership fee.
                </p>

                <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Per Customer */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-medium text-gray-700">Per Additional Customer</label>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">Charged for each approved customer over the plan limit</p>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 font-medium">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.overage_price_per_customer}
                        onChange={(e) => setSettings({ ...settings, overage_price_per_customer: parseFloat(e.target.value) || 0 })}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="text-sm text-gray-500">/ month</span>
                    </div>
                  </div>

                  {/* Per User */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-medium text-gray-700">Per Additional Admin User</label>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">Charged for each admin login over the plan limit</p>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 font-medium">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.overage_price_per_user}
                        onChange={(e) => setSettings({ ...settings, overage_price_per_user: parseFloat(e.target.value) || 0 })}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="text-sm text-gray-500">/ month</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h3 className="font-semibold text-blue-900 text-sm mb-2">How Billing Works</h3>
            <ul className="text-sm text-blue-800 space-y-1.5">
              <li>• <strong>is_paid</strong> = valid CC on file (trial, coupon, or paid — doesn't matter)</li>
              <li>• Failed payments retry 3 times (Day 1, 3, 7) before downgrading</li>
              <li>• Users see a warning banner during the grace period</li>
              <li>• Monthly invoice = Plan price + Customer overages + User overages - Discounts</li>
              <li>• Free plans do not require a credit card</li>
              <li>• Lifetime plans cover membership only — overages are still charged monthly</li>
            </ul>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminMembershipSettings;
